/**
 * ResourceGauge - Circular gauge component for fluid levels
 *
 * Displays a circular progress gauge with:
 * - Percentage fill with color coding based on status
 * - Icon and label
 * - Threshold-based status colors
 */
import React from 'react';
import { Fuel, Droplet, Droplets } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

// Resource configuration
const RESOURCE_CONFIG = {
  fuel_level: { label: 'Carburant', icon: Fuel, color: 'amber' },
  adblue_level: { label: 'AdBlue', icon: Droplets, color: 'blue', dieselOnly: true },
  oil_level: { label: 'Huile', icon: Droplet, color: 'yellow' },
  brake_fluid: { label: 'Liq. frein', icon: Droplet, color: 'rose' },
  coolant_level: { label: 'Refroid.', icon: Droplet, color: 'cyan' },
  washer_fluid: { label: 'Lave-glace', icon: Droplet, color: 'purple' },
};

// Status colors
const STATUS_COLORS = {
  ok: { stroke: '#22c55e', bg: 'bg-green-50', text: 'text-green-700' },
  low: { stroke: '#eab308', bg: 'bg-yellow-50', text: 'text-yellow-700' },
  warning: { stroke: '#f97316', bg: 'bg-orange-50', text: 'text-orange-700' },
  critical: { stroke: '#ef4444', bg: 'bg-red-50', text: 'text-red-700' },
  replace: { stroke: '#7c3aed', bg: 'bg-purple-50', text: 'text-purple-700' },
};

export function ResourceGauge({
  type,
  value = 0,
  status = 'ok',
  lastUpdated,
  onClick,
  size = 'md',
  showLabel = true,
}) {
  const { fmt } = useFormat();
  const config = RESOURCE_CONFIG[type] || { label: type, icon: Droplet, color: 'gray' };
  const statusConfig = STATUS_COLORS[status] || STATUS_COLORS.ok;
  const Icon = config.icon;

  // Size configurations
  const sizes = {
    sm: { container: 'w-16 h-16', radius: 24, stroke: 4, icon: 14, text: 'text-xs' },
    md: { container: 'w-20 h-20', radius: 32, stroke: 5, icon: 18, text: 'text-sm' },
    lg: { container: 'w-24 h-24', radius: 40, stroke: 6, icon: 22, text: 'text-base' },
  };

  const sizeConfig = sizes[size] || sizes.md;
  const circumference = 2 * Math.PI * sizeConfig.radius;
  const progress = Math.min(100, Math.max(0, value));
  const hasData = !!lastUpdated;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={`flex flex-col items-center gap-1 ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      onClick={onClick}
      title={hasData ? `Niveau: ${Math.round(progress)}% — Dernière MAJ: ${fmt.date(lastUpdated)}` : 'Aucune donnée'}
    >
      {/* Circular gauge */}
      <div className={`relative ${sizeConfig.container}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={sizeConfig.radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={sizeConfig.stroke}
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={sizeConfig.radius}
            fill="none"
            stroke={statusConfig.stroke}
            strokeWidth={sizeConfig.stroke}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Icon size={sizeConfig.icon} className={hasData ? statusConfig.text : 'text-gray-400'} />
          <span className={`font-semibold ${sizeConfig.text} ${hasData ? statusConfig.text : 'text-gray-400'}`}>
            {hasData ? `${Math.round(progress)}%` : '—'}
          </span>
        </div>
      </div>

      {/* Label */}
      {showLabel && (
        <span className={`${sizeConfig.text} text-gray-600 text-center font-medium`}>
          {config.label}
        </span>
      )}
    </div>
  );
}

export { RESOURCE_CONFIG };
export default ResourceGauge;
